import { useEffect, useRef, useState } from "react";
import { Maximize2, Minimize2, Volume2, VolumeX, Map as MapIcon, MapPin, HelpCircle, X, ChevronRight, ChevronLeft, ExternalLink, FileText, Move, Circle, Info, Share2 } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { asset } from "@/lib/api";
import { useAmbient } from "@/components/viewer/TourExtras";

export function embedUrl(url) {
  if (!url) return "";
  const yt = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{6,})/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}?rel=0`;
  const vm = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vm) return `https://player.vimeo.com/video/${vm[1]}`;
  return "";
}

export function PinMedia({ pin, light }) {
  const emb = embedUrl(pin.video_url);
  return (
    <>
      {emb && <iframe title="Video" src={emb} className="w-full aspect-video rounded-2xl mt-3 border-0" allow="autoplay; encrypted-media; picture-in-picture; fullscreen" allowFullScreen data-testid="tdv-pin-card-video" />}
      {!emb && pin.video_url && <video src={asset(pin.video_url)} controls playsInline className="w-full rounded-2xl mt-3" data-testid="tdv-pin-card-video" />}
      {pin.pdf_url && <a href={asset(pin.pdf_url)} target="_blank" rel="noreferrer" className={`mt-3 flex items-center gap-2 rounded-xl ${light ? "bg-slate-100 hover:bg-slate-200" : "bg-white/10 hover:bg-white/15"} px-3 py-2 text-sm transition-colors`} data-testid="tdv-pin-card-pdf"><FileText className="w-4 h-4 shrink-0" /><span className="truncate flex-1">{pin.pdf_name || "Open document"}</span><ExternalLink className="w-3.5 h-3.5" /></a>}
    </>
  );
}

export function useTourSound(tour, sceneId, active) {
  const [muted, toggle] = useAmbient(tour.music_url, active);
  const voice = useRef(null);
  const url = tour.scene_audio?.[sceneId];
  useEffect(() => {
    voice.current?.pause();
    if (!active || muted || !url) return undefined;
    const a = new Audio(asset(url));
    voice.current = a;
    a.play().catch(() => {});
    return () => a.pause();
  }, [url, active, muted]);
  const has = !!tour.music_url || Object.values(tour.scene_audio || {}).some(Boolean);
  return { has, muted, toggle };
}

export function useFullscreen() {
  const [on, setOn] = useState(!!document.fullscreenElement);
  useEffect(() => {
    const f = () => setOn(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", f);
    return () => document.removeEventListener("fullscreenchange", f);
  }, []);
  const can = !!document.documentElement.requestFullscreen;
  const toggle = () => (document.fullscreenElement ? document.exitFullscreen() : document.documentElement.requestFullscreen()).catch(() => {});
  return { can, on, toggle };
}

const IconBtn = ({ onClick, title, children, testid }) => (
  <button onClick={onClick} title={title} aria-label={title} className="w-10 h-10 rounded-full hover:bg-white/10 grid place-items-center shrink-0 transition-colors" data-testid={testid}>{children}</button>
);

export function BarTools({ tour, sound, fs, onPlan, onMap, onHelp, onShare }) {
  return (
    <div className="flex items-center" data-testid="tdv-bar-tools">
      {tour.share_button && <IconBtn onClick={onShare} title="Share" testid="tdv-share-button"><Share2 className="w-5 h-5" /></IconBtn>}
      {tour.floorplans?.length > 0 && <IconBtn onClick={onPlan} title="Floor plan" testid="tdv-floorplan-button"><MapIcon className="w-5 h-5" /></IconBtn>}
      {tour.show_map && tour.address && <IconBtn onClick={onMap} title="Location" testid="tdv-map-button"><MapPin className="w-5 h-5" /></IconBtn>}
      {tour.help_card !== false && <IconBtn onClick={onHelp} title="How to explore" testid="tdv-help-button"><HelpCircle className="w-5 h-5" /></IconBtn>}
      {sound.has && <IconBtn onClick={sound.toggle} title={sound.muted ? "Turn sound on" : "Mute sound"} testid="tdv-mute-button">{sound.muted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}</IconBtn>}
      {fs.can && <IconBtn onClick={fs.toggle} title={fs.on ? "Exit full screen" : "Full screen"} testid="tdv-fullscreen-button">{fs.on ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}</IconBtn>}
    </div>
  );
}

const HELP = [
  [Move, "Drag to look around", "Click and drag, or swipe on a phone"],
  [Circle, "Tap circles to move", "Jump to that spot in the tour"],
  [Info, "Tap icons for details", "Photos, videos and documents"],
];

export function HelpCard({ open, onClose, tour, sound, hasPlan }) {
  if (!open) return null;
  const rows = [...HELP, ...(tour.numbered ? [[ChevronRight, "Follow the numbers", "Use Next to see every point in order"]] : []),
    ...(hasPlan ? [[MapIcon, "Use the floor plan", "Tap a dot to jump to that room"]] : []), ...(sound.has ? [[Volume2, "Sound on or off", "Music and voiceover from the top bar"]] : [])];
  return (
    <div className="absolute inset-0 z-40 grid place-items-center bg-black/40 p-4" onClick={onClose} data-testid="tdv-help-card">
      <div className="w-full max-w-md rounded-3xl glass-dark text-white p-6 rise" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4"><p className="font-serif text-2xl">How to explore</p><IconBtn onClick={onClose} title="Close" testid="tdv-help-close"><X className="w-4 h-4" /></IconBtn></div>
        <div className="space-y-3">{rows.map(([I, t, s]) => (
          <div key={t} className="flex items-center gap-3"><span className="w-10 h-10 rounded-full bg-white/10 grid place-items-center shrink-0"><I className="w-5 h-5" /></span><div><p className="text-sm font-semibold">{t}</p><p className="text-xs text-white/60">{s}</p></div></div>
        ))}</div>
        <button onClick={onClose} className="mt-6 w-full h-11 rounded-full bg-white text-slate-900 text-sm font-semibold" data-testid="tdv-help-start">Start exploring</button>
      </div>
    </div>
  );
}

export function PointsNav({ points, current, onGo }) {
  if (points.length < 2) return null;
  const i = points.findIndex((p) => p.id === current);
  const go = (d) => onGo(points[(i + d + points.length) % points.length]);
  return (
    <div className="flex items-center gap-1 rounded-full glass-dark text-white p-1" data-testid="tdv-points-nav">
      <IconBtn onClick={() => go(-1)} title="Previous point" testid="tdv-points-prev"><ChevronLeft className="w-5 h-5" /></IconBtn>
      <span className="text-sm px-2 tabular-nums whitespace-nowrap" data-testid="tdv-points-label">{i >= 0 ? `Point ${i + 1} of ${points.length}` : `${points.length} points`}</span>
      <button onClick={() => go(1)} className="h-10 px-4 rounded-full bg-white text-slate-900 text-sm font-semibold inline-flex items-center gap-1" data-testid="tdv-points-next">{i >= 0 ? "Next" : "Start"}<ChevronRight className="w-4 h-4" /></button>
    </div>
  );
}

export function PlanImage({ plan, pins, labels, current, onPick, onPlace, imgClass = "max-h-[65vh]" }) {
  const here = Object.entries(pins || {}).filter(([, p]) => p.floor === plan.id);
  const click = (e) => {
    if (!onPlace) return;
    const r = e.currentTarget.getBoundingClientRect();
    onPlace({ floor: plan.id, x: ((e.clientX - r.left) / r.width) * 100, y: ((e.clientY - r.top) / r.height) * 100 });
  };
  return (
    <div className={`relative inline-block ${onPlace ? "cursor-crosshair" : ""}`} onClick={click} data-testid={`tdv-plan-${plan.id}`}>
      <img src={asset(plan.url)} alt={plan.name || "Floor plan"} className={`${imgClass} w-auto max-w-full rounded-2xl bg-white select-none`} draggable={false} />
      {here.map(([mid, p]) => (
        <button key={mid} type="button" onClick={(e) => { e.stopPropagation(); onPick?.(mid); }} title={labels[mid]}
          className={`group absolute -translate-x-1/2 -translate-y-1/2 flex items-center gap-1 rounded-full shadow-xl text-xs font-semibold transition-transform hover:scale-110 ${mid === current ? "bg-bp text-white ring-4 ring-white/70" : "bg-slate-900 text-white"}`}
          style={{ left: `${p.x}%`, top: `${p.y}%` }} data-testid={`tdv-plan-pin-${mid}`}>
          <span className="w-4 h-4 m-1 rounded-full bg-white/90" /><span className={`pr-2 ${mid === current ? "" : "hidden group-hover:inline"}`}>{labels[mid]}</span>
        </button>
      ))}
    </div>
  );
}

export function FloorPlanDialog({ open, onClose, tour, labels, current, onPick }) {
  const plans = tour.floorplans || [];
  const [cur, setCur] = useState(plans[0]?.id);
  useEffect(() => { if (open) setCur(tour.plan_pins?.[current]?.floor || plans[0]?.id); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const plan = plans.find((p) => p.id === cur) || plans[0];
  if (!plan) return null;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-5xl bg-slate-950/95 text-white border-white/10 rounded-3xl" data-testid="tdv-floorplan-dialog">
        <DialogTitle className="font-serif text-2xl">Floor plan</DialogTitle>
        {plans.length > 1 && <div className="flex gap-1 rounded-full bg-white/10 p-1 w-fit">{plans.map((p) => <button key={p.id} onClick={() => setCur(p.id)} className={`h-8 px-4 rounded-full text-xs font-semibold ${plan.id === p.id ? "bg-white text-slate-900" : "hover:bg-white/10"}`} data-testid={`tdv-floor-tab-${p.id}`}>{p.name || "Floor"}</button>)}</div>}
        <div className="grid place-items-center"><PlanImage plan={plan} pins={tour.plan_pins} labels={labels} current={current} onPick={(m) => { onPick(m); onClose(); }} /></div>
      </DialogContent>
    </Dialog>
  );
}

export function MapDialog({ open, onClose, address, name }) {
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xl bg-slate-950/95 text-white border-white/10 rounded-3xl" data-testid="tdv-map-dialog">
        <DialogTitle className="font-serif text-2xl">{name}</DialogTitle>
        <p className="text-sm text-white/70 -mt-2" data-testid="tdv-map-address">{address}</p>
        {open && <iframe title="Map" className="w-full h-72 rounded-2xl border-0" loading="lazy" src={`https://www.google.com/maps?q=${encodeURIComponent(address)}&output=embed`} />}
        <a href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}`} target="_blank" rel="noopener noreferrer" className="h-11 rounded-full bg-white text-slate-900 text-sm font-semibold inline-flex items-center justify-center gap-2" data-testid="tdv-map-directions">Get directions<ExternalLink className="w-4 h-4" /></a>
      </DialogContent>
    </Dialog>
  );
}
